import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Package, Receipt, Wrench, BellRing, LogOut, UserCog,
  Settings2, Trash2, MessageSquareText, Building2, ShieldAlert, Landmark,
  Search, PhoneCall, DatabaseZap, Bell, Sparkles, PanelLeftClose, PanelLeftOpen,
  HardDrive, FileCheck2, Wallet, CreditCard, Clock3, FolderClock, Inbox, Shield,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import CommandPalette from "./CommandPalette";
import NotificationBell from "./NotificationBell";
import { useRealtime } from "@/hooks/useRealtime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Sidebar unread badge context — poll + SSE refresh.
// `communication_unread` → red dot on Accounts Communication.
// `payments_attention`   → amber dot on Payment Desk.
// ---------------------------------------------------------------------------
const BadgeCtx = createContext({ comm: 0, payments: 0, refresh: () => {} });

function BadgeProvider({ enabled, children }) {
  const [counts, setCounts] = useState({ comm: 0, payments: 0 });

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await axios.get(`${API}/accounts/sidebar/unread`);
      setCounts({
        comm: r.data?.communication_unread || 0,
        payments: r.data?.payments_attention || 0,
      });
    } catch { /* noop — badges are non-essential */ }
  }, [enabled]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!enabled) return;
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
  }, [enabled, refresh]);

  // Live refresh on any chat/notification/reminder event.
  useRealtime((ev) => {
    if (!enabled) return;
    if (ev?.type === "chat" || ev?.type === "notification" || ev?.type === "reminder") {
      refresh();
    }
  }, { enabled });

  return (
    <BadgeCtx.Provider value={{ comm: counts.comm, payments: counts.payments, refresh }}>
      {children}
    </BadgeCtx.Provider>
  );
}

export function useAccountsBadges() { return useContext(BadgeCtx); }

const OPS_NAV = [
  { to: "/",           label: "Dashboard",  icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers",  label: "Customers",  icon: Users,           testid: "nav-customers" },
  { to: "/crm/enquiries", label: "Enquiries", icon: Inbox,         testid: "nav-enquiries" },
  { to: "/engagement", label: "Engagement", icon: PhoneCall,       testid: "nav-engagement" },
];
const BIZ_NAV = [
  { to: "/products",  label: "Inventory",    icon: Package,  testid: "nav-products" },
  { to: "/sales",     label: "Sales",        icon: Receipt,  testid: "nav-sales" },
  { to: "/subsidies", label: "Subsidy Desk", icon: Landmark, testid: "nav-subsidies" },
  { to: "/services",  label: "Service",      icon: Wrench,   testid: "nav-services" },
  { to: "/followups", label: "Follow-ups",   icon: BellRing, testid: "nav-followups" },
];
const ACCOUNTS_NAV = [
  { to: "/accounts/reminders",  label: "Payment Desk",        icon: Wallet,            testid: "nav-accounts-reminders", badge: "payments" },
  { to: "/accounts/payments",   label: "Payments",            icon: CreditCard,        testid: "nav-accounts-payments" },
  { to: "/accounts/recurring",  label: "Recurring Reminders", icon: FolderClock,       testid: "nav-accounts-recurring" },
  { to: "/accounts/chat",       label: "Accounts Communication", icon: MessageSquareText, testid: "nav-accounts-chat", badge: "comm" },
  { to: "/accounts/payees",     label: "Payees",              icon: Users,             testid: "nav-accounts-payees" },
  { to: "/accounts/banks",      label: "Bank Accounts",       icon: Landmark,          testid: "nav-accounts-banks" },
  { to: "/accounts/activity",   label: "Activity",            icon: Clock3,            testid: "nav-accounts-activity" },
];
// OWNER_NAV = business-oriented, no system-admin clutter
const OWNER_BIZ_NAV = [
  { to: "/",           label: "Dashboard",   icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers",  label: "Customers",   icon: Users,           testid: "nav-customers" },
  { to: "/crm/enquiries", label: "Enquiries", icon: Inbox,          testid: "nav-enquiries" },
  { to: "/sales",      label: "Sales",       icon: Receipt,         testid: "nav-sales" },
  { to: "/services",   label: "Service",     icon: Wrench,          testid: "nav-services" },
  { to: "/subsidies",  label: "Subsidy",     icon: Landmark,        testid: "nav-subsidies" },
  { to: "/engagement", label: "Calling",     icon: PhoneCall,       testid: "nav-engagement" },
];
const ADMIN_NAV = [
  { to: "/admin/access-control",        label: "Access Control", icon: Shield,            testid: "nav-access-control", adminOnly: true },
  { to: "/users",                       label: "Team",           icon: UserCog,           testid: "nav-users" },
  { to: "/admin/team-access",           label: "Team & Access",  icon: ShieldAlert,       testid: "nav-team-access" },
  { to: "/settings/customer-fields",    label: "Fields",         icon: Settings2,         testid: "nav-fields" },
  { to: "/settings/notifications",      label: "Messages",       icon: MessageSquareText, testid: "nav-notifications" },
  { to: "/settings/call-outcomes",      label: "Call Outcomes",  icon: PhoneCall,         testid: "nav-call-outcomes" },
  { to: "/admin/data-migration",        label: "Data Migration", icon: DatabaseZap,       testid: "nav-data-migration" },
  { to: "/admin/historical-processing", label: "Historical Queue", icon: FileCheck2,      testid: "nav-historical-processing" },
  { to: "/admin/backups",               label: "Backups",        icon: HardDrive,         testid: "nav-backups" },
  { to: "/settings/business",           label: "Business",       icon: Building2,         testid: "nav-business" },
  { to: "/settings/security-log",       label: "Security",       icon: ShieldAlert,       testid: "nav-security-log" },
  { to: "/trash",                       label: "Trash",          icon: Trash2,            testid: "nav-trash" },
];

/* Nav item — renders either an expanded row (icon + label) or a compact
   icon-only pill when the sidebar is collapsed. `badge` can be either
   "comm" (red dot — unread chat) or "payments" (amber dot — attention). */
function NavItem({ to, label, icon: Icon, testid, collapsed, badge }) {
  const { comm, payments } = useAccountsBadges();
  const count = badge === "comm" ? comm : (badge === "payments" ? payments : 0);
  const dotColor = badge === "comm" ? "bg-[#FF4D4F]" : "bg-[#FFB020]";
  const ariaLabel = count > 0
    ? `${label} — ${count} ${badge === "comm" ? "unread" : "attention"}`
    : label;
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={testid}
      data-badge-count={count || undefined}
      title={collapsed ? ariaLabel : undefined}
      aria-label={ariaLabel}
      className={({ isActive }) =>
        `group relative flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2 rounded-[10px] text-[13px] font-medium tracking-tight transition-all duration-200 ${
          isActive
            ? "bg-[#1A1F1C] text-[#39FF14] shadow-[inset_2px_0_0_0_rgba(57,255,20,0.9)]"
            : "text-[#8A8F98] hover:text-[#F8F9FA] hover:bg-[#121815]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className="relative shrink-0">
            <Icon size={16} className={isActive ? "text-[#39FF14]" : "text-[#8A8F98] group-hover:text-[#F8F9FA]"} />
            {count > 0 && collapsed && (
              <span className={`absolute -top-1 -right-1 w-2 h-2 ${dotColor} rounded-full ring-2 ring-[#0B0F0C]`}
                    data-testid={`${testid}-badge-dot`} />
            )}
          </span>
          {!collapsed && <span className="truncate flex-1">{label}</span>}
          {!collapsed && count > 0 && (
            <span className={`shrink-0 min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${
              badge === "comm" ? "bg-[#FF4D4F] text-white" : "bg-[#FFB020] text-[#0B0F0C]"
            }`} data-testid={`${testid}-badge-count`}>
              {count > 99 ? "99+" : count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function NavGroup({ title, items, collapsed }) {
  return (
    <div className="mb-4">
      {!collapsed && (
        <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.15em] font-heading text-[#8A8F98]">{title}</div>
      )}
      {collapsed && <div className="mx-3 mb-2 h-px bg-[#3A4640]" aria-hidden />}
      <div className="space-y-0.5">
        {items.map((it) => <NavItem key={it.to} {...it} collapsed={collapsed} />)}
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("kova_sidebar_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("kova_sidebar_collapsed", collapsed ? "1" : "0"); } catch (_) {}
  }, [collapsed]);
  const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const role = user?.role;
  const isAdmin = role === "admin";
  const isOwner = role === "owner" || role === "co_admin";
  const isAccountant = role === "accountant";
  const canSeeAccounts = isAdmin || isOwner || isAccountant;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || user?.email || "?")
    .split(/[.\s@]/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase()).join("") || "U";

  return (
    <BadgeProvider enabled={canSeeAccounts}>
    <div className="min-h-screen flex bg-[#0B0F0C] text-[#F8F9FA]">
      {/* Sidebar */}
      <aside
        className={`shrink-0 bg-[#0B0F0C] border-r border-[#3A4640] flex flex-col transition-[width] duration-200 ${collapsed ? "w-[72px]" : "w-[240px]"}`}
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {/* Brand — TZAT · KOVA lockup (compact when collapsed) */}
        <div className={`border-b border-[#3A4640] ${collapsed ? "px-3 py-4" : "px-5 py-5"}`}>
          <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
            <div className="w-10 h-10 rounded-[10px] bg-[#121815] border border-[#3A4640] flex items-center justify-center overflow-hidden shrink-0">
              <img src="/logo.jpg" alt="Three Zee Agro Tech" className="w-full h-full object-cover" />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-[0.28em] text-[#8A8F98] font-heading font-semibold leading-none">TZAT</div>
                <div className="font-heading font-bold text-[17px] leading-tight tracking-tight text-[#F8F9FA] mt-1">KOVA</div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#39FF14] mt-0.5">Ops Platform</div>
              </div>
            )}
          </div>
        </div>

        {/* Groups */}
        <nav className={`flex-1 overflow-y-auto ${collapsed ? "px-2 py-3" : "px-2 py-4"}`}>
          {isAccountant ? (
            // Focused Accountant workspace — Accounts-only.
            <NavGroup title="Accounts" items={ACCOUNTS_NAV} collapsed={collapsed} />
          ) : isOwner ? (
            // Clean Owner / Management workspace — business-first, no system clutter.
            <>
              <NavGroup title="Management" items={OWNER_BIZ_NAV} collapsed={collapsed} />
              <NavGroup title="Accounts"   items={ACCOUNTS_NAV}  collapsed={collapsed} />
            </>
          ) : (
            // Admin + all other roles — full workspace.
            <>
              <NavGroup title="Operations" items={OPS_NAV}  collapsed={collapsed} />
              <NavGroup title="Business"   items={BIZ_NAV}  collapsed={collapsed} />
              {canSeeAccounts && <NavGroup title="Accounts" items={ACCOUNTS_NAV} collapsed={collapsed} />}
              {isAdmin && <NavGroup title="Admin" items={ADMIN_NAV} collapsed={collapsed} />}
            </>
          )}
        </nav>

        {/* Collapse toggle */}
        <div className="px-2 border-t border-[#3A4640]">
          <button
            type="button"
            onClick={() => setCollapsed((s) => !s)}
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`w-full my-2 flex items-center ${collapsed ? "justify-center" : "gap-2 justify-start"} px-3 py-2 rounded-[10px] text-[#8A8F98] hover:text-[#F8F9FA] hover:bg-[#121815] text-[12px] transition-colors`}
          >
            {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>

        {/* User panel */}
        <div className="p-3 border-t border-[#3A4640]">
          <div className={`flex items-center gap-3 px-2 py-2 rounded-[10px] bg-[#121815] border border-[#3A4640] ${collapsed ? "justify-center" : ""}`}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#39FF14] to-[#2ECC71] text-[#0B0F0C] font-heading font-bold text-xs flex items-center justify-center shrink-0">
              {initials}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate text-[#F8F9FA]" data-testid="current-user-email">
                  {user?.name || user?.email}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#39FF14]">
                  <span className="tzat-pulse" />
                  {isAdmin ? "Administrator" : (isOwner ? "Owner" : isAccountant ? "Accountant" : (user?.role || "Sales Rep"))}
                </div>
              </div>
            )}
          </div>
          {collapsed ? (
            <button
              type="button"
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Sign out"
              className="w-full mt-2 flex items-center justify-center px-3 py-2 rounded-[10px] text-[#8A8F98] hover:text-[#F8F9FA] hover:bg-[#121815] transition-colors"
            >
              <LogOut size={14} />
            </button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full mt-2 justify-start text-[#8A8F98] hover:text-[#F8F9FA] hover:bg-[#121815] rounded-[10px]"
            >
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          )}
        </div>
      </aside>

      {/* Content column */}
      <main className="flex-1 overflow-x-auto min-w-0 relative">
        {/* Top bar */}
        <div className="sticky top-0 z-30 flex items-center justify-between gap-3 px-6 py-3 bg-[#0B0F0C]/85 backdrop-blur-md border-b border-[#3A4640]">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            data-testid="open-command-palette-btn"
            className="group flex items-center gap-2.5 min-w-[280px] max-w-[520px] w-full sm:w-[420px] px-3 py-2 bg-[#121815] hover:bg-[#1A1F1C] border border-[#3A4640] hover:border-[#39FF14]/40 rounded-[10px] text-left text-[#8A8F98] hover:text-[#F8F9FA] transition-colors"
            title="Search anything (Ctrl / ⌘ K)"
          >
            <Search size={14} className="text-[#8A8F98] group-hover:text-[#39FF14] transition-colors" />
            <span className="flex-1 text-sm truncate">Search customers, machines, invoices…</span>
            <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-mono tracking-wider text-[#8A8F98] bg-[#0B0F0C] border border-[#3A4640] rounded px-1.5 py-0.5">
              {isMac ? "⌘" : "Ctrl"} K
            </kbd>
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="topbar-ai"
              className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-2 rounded-[10px] border border-[#3A4640] text-[#8A8F98] hover:text-[#39FF14] hover:border-[#39FF14]/40 transition-colors"
              title="TZAT AI (coming soon)"
            >
              <Sparkles size={14} />
              <span className="text-xs font-heading uppercase tracking-wider">TZAT AI</span>
            </button>
            <NotificationBell />
          </div>
        </div>

        <div className="relative z-0">
          <Outlet />
        </div>
      </main>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
    </BadgeProvider>
  );
}
